import i18next from 'i18next';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    createVendorArticle,
    createVendorArticlePrice,
    deleteVendorArticle,
    deleteVendorArticlePrice,
    getVendorArticlePrices,
    getVendorArticles,
    updateVendorArticle,
    updateVendorArticlePrice,
} from 'src/api/article-management';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';
import {
    CREATE_VENDOR_ARTICLE,
    CREATE_VENDOR_ARTICLE_PRICE,
    DELETE_VENDOR_ARTICLE,
    DELETE_VENDOR_ARTICLE_PRICE,
    fetchVendorArticlePrices,
    fetchVendorArticles,
    FETCH_VENDOR_ARTICLES,
    FETCH_VENDOR_ARTICLE_PRICES,
    requestCreateVendorArticle,
    requestCreateVendorArticleFailure,
    requestCreateVendorArticlePrice,
    requestCreateVendorArticlePriceFailure,
    requestCreateVendorArticlePriceSuccess,
    requestCreateVendorArticleSuccess,
    requestDeleteVendorArticle,
    requestDeleteVendorArticleFailure,
    requestDeleteVendorArticlePrice,
    requestDeleteVendorArticlePriceFailure,
    requestDeleteVendorArticlePriceSuccess,
    requestDeleteVendorArticleSuccess,
    requestUpdateVendorArticle,
    requestUpdateVendorArticleFailure,
    requestUpdateVendorArticlePrice,
    requestUpdateVendorArticlePriceFailure,
    requestUpdateVendorArticlePriceSuccess,
    requestUpdateVendorArticleSuccess,
    requestVendorArticlePrices,
    requestVendorArticlePricesFailure,
    requestVendorArticlePricesSuccess,
    requestVendorArticles,
    requestVendorArticlesFailure,
    requestVendorArticlesSuccess,
    UPDATE_VENDOR_ARTICLE,
    UPDATE_VENDOR_ARTICLE_PRICE,
} from './actions';

/* Vendor Article */
function* fetchVendorArticlesSaga(action: AnyAction): SagaIterator {
    yield put(requestVendorArticles());
    const { error, response } = yield call(getVendorArticles, action.articleId);
    if (error) {
        yield put(requestVendorArticlesFailure(error));
    } else {
        yield put(requestVendorArticlesSuccess(response));
    }
}

function* createVendorArticleSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateVendorArticle());
    const { error, response } = yield call(createVendorArticle, action.vendorArticle);
    if (error) {
        yield put(requestCreateVendorArticleFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticle.create.error') + '\n' + error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestCreateVendorArticleSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticle.create.success'),
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchVendorArticles(action.vendorArticle.articleID));
    }
}

function* updateVendorArticleSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateVendorArticle());
    const { error, response } = yield call(updateVendorArticle, action.vendorArticle);
    if (error) {
        yield put(requestUpdateVendorArticleFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticle.update.error') + '\n' + error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestUpdateVendorArticleSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticle.update.success'),
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchVendorArticles(action.vendorArticle.articleID));
    }
}

function* deleteVendorArticleSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteVendorArticle());
    const { error, response } = yield call(deleteVendorArticle, action.vendorArticle.id);
    if (error) {
        yield put(requestDeleteVendorArticleFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticle.delete.error') + '\n' + error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestDeleteVendorArticleSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticle.delete.success'),
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchVendorArticles(action.vendorArticle.articleID));
    }
}

export function* generateFetchVendorArticlesWatcher() {
    yield takeLatest(FETCH_VENDOR_ARTICLES, fetchVendorArticlesSaga);
}

export function* generateCreateVendorArticleWatcher() {
    yield takeLatest(CREATE_VENDOR_ARTICLE, createVendorArticleSaga);
}

export function* generateUpdateVendorArticleWatcher() {
    yield takeLatest(UPDATE_VENDOR_ARTICLE, updateVendorArticleSaga);
}

export function* generateDeleteVendorArticleWatcher() {
    yield takeLatest(DELETE_VENDOR_ARTICLE, deleteVendorArticleSaga);
}

/* Vendor Article Price */
function* fetchVendorArticlePricesSaga(action: AnyAction): SagaIterator {
    yield put(requestVendorArticlePrices());
    const { error, response } = yield call(getVendorArticlePrices, action.articleId, action.vendorId, action.showPast);
    if (error) {
        yield put(requestVendorArticlePricesFailure(error));
    } else {
        yield put(requestVendorArticlePricesSuccess(response));
    }
}

function* createVendorArticlePriceSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateVendorArticlePrice());
    const { error, response } = yield call(createVendorArticlePrice, action.vendorArticlePrice);
    if (error) {
        yield put(requestCreateVendorArticlePriceFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.vendorArticlePrice.create.error') + '\n' + error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestCreateVendorArticlePriceSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticlePrice.create.success'),
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchVendorArticlePrices(action.articleId, action.vendorId, false));
    }
}

function* updateVendorArticlePriceSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateVendorArticlePrice());
    const { error, response } = yield call(updateVendorArticlePrice, action.vendorArticlePrice);
    if (error) {
        yield put(requestUpdateVendorArticlePriceFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.vendorArticlePrice.update.error') + '\n' + error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestUpdateVendorArticlePriceSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticlePrice.update.success'),
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchVendorArticlePrices(action.articleId, action.vendorId, false));
    }
}

function* deleteVendorArticlePriceSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteVendorArticlePrice());
    const { error, response } = yield call(deleteVendorArticlePrice, action.vendorArticlePriceId);
    if (error) {
        yield put(requestDeleteVendorArticlePriceFailure(error));
        yield put(
            enqueueSnackbarAction({
                message:
                    i18next.t('articleManagement:pages.article.vendorArticlePrice.delete.error') + '\n' + error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestDeleteVendorArticlePriceSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('articleManagement:pages.article.vendorArticlePrice.delete.success'),
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(fetchVendorArticlePrices(action.articleId, action.vendorId, false));
    }
}

export function* generateFetchVendorArticlePricesWatcher() {
    yield takeLatest(FETCH_VENDOR_ARTICLE_PRICES, fetchVendorArticlePricesSaga);
}

export function* generateCreateVendorArticlePriceWatcher() {
    yield takeLatest(CREATE_VENDOR_ARTICLE_PRICE, createVendorArticlePriceSaga);
}

export function* generateUpdateVendorArticlePriceWatcher() {
    yield takeLatest(UPDATE_VENDOR_ARTICLE_PRICE, updateVendorArticlePriceSaga);
}

export function* generateDeleteVendorArticlePriceWatcher() {
    yield takeLatest(DELETE_VENDOR_ARTICLE_PRICE, deleteVendorArticlePriceSaga);
}
